import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Activity, HeartPulse, LockKeyhole, ShieldCheck } from "lucide-react";
import "@/aion-video-capabilities.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  getPersonalHealthDashboard,
  getPersonalHealthStatus,
  lockPersonalHealth,
  unlockPersonalHealth,
  updatePersonalHealthPrivacy,
} from "@/services/personalHealth";

const METRIC_LABELS = {
  steps: ["Schritte", ""],
  active_minutes: ["Aktive Minuten", " min"],
  heart_rate_bpm: ["Puls", " bpm"],
  resting_heart_rate_bpm: ["Ruhepuls", " bpm"],
  sleep_hours: ["Schlaf", " h"],
  calories_active: ["Aktive Kalorien", " kcal"],
  distance_km: ["Distanz", " km"],
  workouts_7d: ["Workouts / 7 Tage", ""],
  spo2_percent: ["SpO₂", " %"],
  hrv_ms: ["HRV", " ms"],
};

export const PersonalHealthPanel = ({ token }) => {
  const [status, setStatus] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [grant, setGrant] = useState("");
  const grantRef = useRef("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  const loadStatus = useCallback(async () => {
    try {
      const next = await getPersonalHealthStatus(token);
      setStatus(next);
    } catch (statusError) {
      setError(statusError.message || "Health-Status konnte nicht geladen werden.");
    }
  }, [token]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (!grant) return undefined;
    const activeGrant = grant;
    const timeout = window.setTimeout(() => {
      void lockPersonalHealth(activeGrant, token).catch(() => null);
      setGrant("");
      setDashboard(null);
    }, 5 * 60 * 1000);
    return () => window.clearTimeout(timeout);
  }, [grant, token]);

  useEffect(() => {
    grantRef.current = grant;
  }, [grant]);

  useEffect(() => () => {
    const activeGrant = grantRef.current;
    grantRef.current = "";
    if (activeGrant) {
      void lockPersonalHealth(activeGrant, token).catch(() => null);
    }
  }, [token]);

  const metrics = useMemo(() => {
    const values = dashboard?.snapshot?.metrics || {};
    return Object.entries(values).filter(([key, value]) => METRIC_LABELS[key] && value !== null && value !== undefined);
  }, [dashboard]);

  const handleUnlock = async () => {
    if (!password) return;
    setPending(true);
    setError("");
    let unlockedGrant = "";
    try {
      const unlocked = await unlockPersonalHealth(password, token);
      unlockedGrant = unlocked.grant || "";
      setPassword("");
      setGrant(unlockedGrant);
      const nextDashboard = await getPersonalHealthDashboard(unlockedGrant, token);
      setDashboard(nextDashboard);
      await loadStatus();
    } catch (unlockError) {
      if (unlockedGrant) {
        await lockPersonalHealth(unlockedGrant, token).catch(() => null);
      }
      setPassword("");
      setGrant("");
      setDashboard(null);
      setError(unlockError.message || "Health-Bereich konnte nicht entsperrt werden.");
    } finally {
      setPending(false);
    }
  };

  const handlePrivacyToggle = async () => {
    if (!grant) return;
    setPending(true);
    setError("");
    try {
      const updated = await updatePersonalHealthPrivacy(!status?.context_hints_enabled, grant, token);
      setStatus((previous) => ({ ...(previous || {}), context_hints_enabled: updated.context_hints_enabled }));
    } catch (privacyError) {
      if (privacyError.status === 401) {
        setGrant("");
        setDashboard(null);
      }
      setError(privacyError.message || "Health-Datenschutz konnte nicht aktualisiert werden.");
    } finally {
      setPending(false);
    }
  };

  const handleLock = async () => {
    const activeGrant = grant;
    setGrant("");
    setDashboard(null);
    setPassword("");
    setError("");
    if (!activeGrant) return;
    try {
      await lockPersonalHealth(activeGrant, token);
    } catch (lockError) {
      setError(lockError.message || "Health-Zugriff wurde lokal gesperrt; Server-Widerruf konnte nicht bestätigt werden.");
    }
  };

  return (
    <section className="aion-health-panel" data-testid="personal-health-panel">
      <div className="aion-health-header">
        <div>
          <p className="jarvis-eyebrow">PRIVATE HEALTH VAULT</p>
          <h2 className="jarvis-title">Persönliche Gesundheit</h2>
          <p className="aion-health-subtitle">Sensible Wellness-Daten bleiben gesperrt, bis Du Dich erneut bestätigst.</p>
        </div>
        <div className={`aion-health-lock-state${grant ? " aion-health-lock-state-open" : ""}`}>
          {grant ? <ShieldCheck size={18} aria-hidden="true" /> : <LockKeyhole size={18} aria-hidden="true" />}
          <span>{grant ? "Entsperrt" : "Gesperrt"}</span>
        </div>
      </div>

      {error ? <div className="jarvis-inline-error" data-testid="personal-health-error">{error}</div> : null}

      {!grant ? (
        <div className="aion-health-lock-card" data-testid="personal-health-locked">
          <div className="aion-health-lock-copy">
            <HeartPulse size={28} aria-hidden="true" />
            <div>
              <strong>Health-Daten sind privat</strong>
              <p>Zum Öffnen wird Dein bestehendes AION-Passwort erneut geprüft. Der Zugriff läuft automatisch nach wenigen Minuten ab.</p>
            </div>
          </div>
          <div className="aion-health-unlock-row">
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              onKeyDown={(event) => { if (event.key === "Enter") void handleUnlock(); }}
              placeholder="AION-Passwort"
              data-testid="personal-health-password"
            />
            <Button onClick={handleUnlock} disabled={pending || !password} data-testid="personal-health-unlock-button">
              {pending ? "Prüfe…" : "Öffnen"}
            </Button>
          </div>
          <div className="aion-note">AION zeigt hier keine Demo-Gesundheitswerte. Daten erscheinen erst, wenn eine reale Quelle oder ein bestätigter Import vorhanden ist.</div>
        </div>
      ) : (
        <div className="aion-health-unlocked" data-testid="personal-health-unlocked">
          <div className="aion-health-toolbar">
            <div>
              <strong>{dashboard?.connected ? dashboard?.snapshot?.source_label || dashboard?.snapshot?.source : "Keine Health-Quelle verbunden"}</strong>
              <small>{dashboard?.snapshot?.captured_at ? `Letzte Messung: ${new Date(dashboard.snapshot.captured_at).toLocaleString()}` : "Noch keine realen Messdaten vorhanden"}</small>
            </div>
            <Button size="sm" variant="outline" onClick={() => void handleLock()} data-testid="personal-health-lock-button">Sperren</Button>
          </div>

          {metrics.length ? (
            <div className="aion-health-metric-grid" data-testid="personal-health-metrics">
              {metrics.map(([key, value]) => {
                const [label, unit] = METRIC_LABELS[key];
                return (
                  <div className="aion-health-metric" key={key} data-testid={`personal-health-metric-${key}`}>
                    <Activity size={18} aria-hidden="true" />
                    <span>{label}</span>
                    <strong>{value}{unit}</strong>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="aion-health-empty" data-testid="personal-health-empty">
              <HeartPulse size={28} aria-hidden="true" />
              <div>
                <strong>Noch keine echten Health-Daten</strong>
                <p>Apple Health, Health Connect, Garmin, Fitbit, Withings oder ein anderer Provider kann später über die geschützte Provider-Pipeline angebunden werden.</p>
              </div>
            </div>
          )}

          <div className="aion-health-privacy-row">
            <div>
              <strong>Proaktive Health-Hinweise</strong>
              <small>Nur mit Opt-in. Der Health-Hook erhält ein normalisiertes Signal wie „Workout bereit“ – kein Rohbild wird im Health-Vault gespeichert.</small>
            </div>
            <Button size="sm" variant={status?.context_hints_enabled ? "default" : "outline"} onClick={handlePrivacyToggle} disabled={pending} data-testid="personal-health-context-toggle">
              {status?.context_hints_enabled ? "Aktiv" : "Aus"}
            </Button>
          </div>
          <div className="aion-note">Der Health-Unlock ist ausschließlich eine Privacy-Sperre per Passwort-Reauth und gilt nicht als Human Key, MFA oder Strong Auth.</div>
        </div>
      )}
    </section>
  );
};